import styled from 'styled-components';
import React, { useEffect, useRef, useState } from 'react';
import { TTypedTFunction, useTranslation } from '@lib/useTypedTranslation';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';

import { NoSelectionOverlay } from '../card/noSelectionOverlay';
import { AmChart, coreTheme } from '../chart/amChart';
import { addLegend } from './lib';
import { Theme } from '../../containers/app/themes';

import './columnDateChart.css';


const Container = styled.div<{ bottomBorder: boolean }>`
  position: relative;
  margin-top: 1.5rem;
  ${({ bottomBorder }) => bottomBorder && `border-bottom: 3px solid #edf1f3;`}
`;

const Header = styled.div<{ noMarginBottom?: boolean }>`
  display: flex;
  justify-content: space-between;
  ${({ noMarginBottom }) => !noMarginBottom && `margin-bottom: 1.95rem;`}
`;

const Title = styled.h1`
  font-weight: ${({ theme }: { theme: Theme }) => theme.font.weights.bold};
  font-size: ${({ theme }: { theme: Theme }) => theme.font.sizes.sixteenPixels};
  width: 65%;
  margin-bottom: 0;
`;

const ChartContainer = styled.div<{ length?: number }>`
  width: 95%;
  margin-left: auto;
  margin-right: auto;
  height: 14rem;
  ${({ length }) => length && `width: ${length}%;`}
`;

const NoDataOverlay = styled(NoSelectionOverlay)`
  padding: 0;
`;

export interface LineChartSeries<Data extends { local: number }> {
  visible: boolean,
  dataKey: keyof Omit<Data, 'local'>,
  description: string,
  colour: string,
  bulletsOnly?: boolean
}

export interface IProps<Data extends { local: number }> {
  data: Data[] | null,
  className?: string,
  series: LineChartSeries<Data>[],
  title: string | JSX.Element,
  cypressId: string,
  bottomBorder?: boolean,
  onMount?: (chart: am4charts.XYChart) => am4charts.XYChart,
  drawYAxis?: (chart: am4charts.XYChart, axis: am4charts.ValueAxis | am4charts.DurationAxis, enabledSeries?: any[]) => void,
  hasLegend: boolean,
  drawXAxis: (chart: am4charts.XYChart, xAxis: am4charts.DateAxis) => void,
  hasNoSelectionOverlay?: boolean,
  useAxisTooltip?: boolean,
  adjustChartDimensions?: {length?: number, height: number},
  getSeriesToolTipAdapter?: () => (_: any, target: am4charts.LineSeries) => string,
  invertedXAxis?: boolean
}

export function LineChart<Data extends { local: number }>(props: IProps<Data>) {
  const [chartReady, setChartReady] = useState(false);
  const chartRef = useRef(null);
  const ns = 'translation';
  const { t } = useTranslation(['performance', 'editEvents', 'translation', 'deviceApplications', ns, 'timeState']);
  const { series: allSeries, drawYAxis } = props;

  useEffect(() => {
    allSeries.forEach((seriesData) => {
      const series = chartRef.current.map.getKey(seriesData.dataKey);
      if (seriesData.visible) {
        series.show();
        series.hiddenInLegend = false;
      } else {
        series.hide();
        series.hiddenInLegend = true;
      }

      if (!props.useAxisTooltip) {
        series.dummyData.allSeries = allSeries;
      }
    });
    drawYAxis && drawYAxis(chartRef.current, chartRef.current?.dummyData?.yAxis, allSeries.filter(series => series.visible).map(series => series.dataKey) as string[]);

  }, [allSeries, drawYAxis, props.data, props.useAxisTooltip, chartRef.current?.dummyData?.yAxis]);

  function createChart(id: string) {
    am4core.useTheme(coreTheme);

    const chart = am4core.create(id, am4charts.XYChart);
    chart.paddingTop = 30;
    chart.minHeight = props?.adjustChartDimensions?.height ?? 220;

    const xAxis = chart.xAxes.push(new am4charts.DateAxis());

    xAxis.dataFields.date = 'local';
    xAxis.groupData = false;
    xAxis.cursorTooltipEnabled = false;
    xAxis.renderer.grid.template.disabled = true;
    xAxis.renderer.minGridDistance = 50;

    if (props.invertedXAxis) {
      xAxis.renderer.opposite = true;
    }

    if (props.useAxisTooltip) {
      chart.cursor = new am4charts.XYCursor();
      chart.cursor.lineY.disabled = true;
      chart.cursor.lineX.disabled = true;
      chart.cursor.behavior = 'none';
    }

    const yAxis = chart.yAxes.push(new am4charts.ValueAxis());
    yAxis.min = 0;
    yAxis.renderer.minGridDistance = 30;
    yAxis.contentAlign = 'right';
    yAxis.tooltip.disabled = true;

    props.series.forEach((series) => {
      createSeries(series.dataKey, series.description, series.colour, t, chart, props.getSeriesToolTipAdapter, series.bulletsOnly);
    });

    chart.zoomOutButton.disabled = true;
    chart.dummyData = {
      yAxis,
      xAxis
    };

    if (props.hasLegend) {
      addLegend(chart, props.invertedXAxis);
    }

    chartRef.current = chart;
    return chart;
  }

  function onMount(chart: am4charts.XYChart) {
    props.drawXAxis(chart, chart.xAxes.values[0] as am4charts.DateAxis);
    props.onMount && props.onMount(chart);
    return chart;
  }

  return (
    <Container className={props.className || ''} bottomBorder={props.bottomBorder} data-id={`${props.cypressId}${chartReady ? '--chart-ready' : ''}`}>
      <Header className="header" noMarginBottom={props.invertedXAxis}>
        {props.hasNoSelectionOverlay !== false && <NoDataOverlay noSelectionText={t('NO_DATA_AVAILABLE', { ns })} show={Boolean(!props.data || props.data.length === 0)} />}
        <Title>{props.title}</Title>
      </Header>
      <ChartContainer className = {"LineChart"} length={props?.adjustChartDimensions?.length}>
        <AmChart
          tag={props.cypressId}
          chartProvider={createChart}
          dataProvider={() => props.data}
          onMount={onMount}
          onDataValidated={() => setChartReady(true)}
          link={props.data}
          cypressId={props.cypressId}
        />
      </ChartContainer>
    </Container>
  );
}

export function createSeries(field: any, name: string, colour: string, t: TTypedTFunction, chart: am4charts.XYChart, getSeriesToolTipAdapter?: any, bulletsOnly?: boolean) {
  const series = chart.series.push(new am4charts.LineSeries());
  series.id = field as unknown as string;
  series.name = name;
  series.dataFields.valueY = field as unknown as string;

  series.dataFields.dateX = 'local';
  series.name = name;
  series.connect = false;
  series.autoGapCount = 600;
  series.stroke = am4core.color(colour);

  if (bulletsOnly) {
    series.strokeWidth = 0;
    series.fillOpacity = 0;
    const bullet = series.bullets.push(new am4charts.CircleBullet());
    bullet.circle.radius = 1.5;
    bullet.circle.fill = am4core.color(colour);
    bullet.circle.strokeWidth = 0;
  } else {
    series.strokeWidth = 2;
    series.fill = am4core.color(colour);
    series.fillOpacity = 0.2;
  }

  series.tooltipHTML = '';
  series.tooltip.pointerOrientation = "right";

  if (getSeriesToolTipAdapter) {
    series.adapter.add('tooltipHTML', getSeriesToolTipAdapter());
  }

  series.dummyData = {
    colour: am4core.color(colour)
  };
  return series;
}
